/** How far below a picked element its visible box is looked for. */
const MARK_DEPTH = 4;

const zero = (value: string) => !(Number.parseFloat(value) > 0);

/** Whether the element draws nothing of its own: no fill, no border, no shadow, no rounding. */
function unpainted(style: CSSStyleDeclaration): boolean {
  const fill = style.backgroundColor;
  return (
    (!fill || fill === 'transparent' || fill === 'rgba(0, 0, 0, 0)') &&
    (!style.backgroundImage || style.backgroundImage === 'none') &&
    (!style.boxShadow || style.boxShadow === 'none') &&
    zero(style.borderTopWidth) &&
    zero(style.borderRightWidth) &&
    zero(style.borderBottomWidth) &&
    zero(style.borderLeftWidth) &&
    zero(style.borderTopLeftRadius) &&
    zero(style.borderTopRightRadius) &&
    zero(style.borderBottomRightRadius) &&
    zero(style.borderBottomLeftRadius)
  );
}

/** The one child that is all the element holds: no sibling, no text of the element's own. */
function onlyChild(element: Element): Element | null {
  if (element.shadowRoot || element.children.length !== 1) return null;
  for (const node of element.childNodes)
    if (node.nodeType === Node.TEXT_NODE && node.nodeValue?.trim()) return null;
  return element.children[0];
}

function sameBox(a: Element, b: Element): boolean {
  const first = a.getBoundingClientRect();
  const second = b.getBoundingClientRect();
  return (
    Math.abs(first.left - second.left) <= 1 &&
    Math.abs(first.top - second.top) <= 1 &&
    Math.abs(first.right - second.right) <= 1 &&
    Math.abs(first.bottom - second.bottom) <= 1
  );
}

/**
 * The element whose box shows a pick. A wrapper that draws nothing (a link around its item, a
 * `display: contents` shell) hands the mark to the one child that fills it: the mark takes the
 * shape the user sees, whichever element carries the meaning.
 */
export function pickMarkElement(target: Element): Element {
  const view = target.ownerDocument.defaultView;
  if (!view) return target;
  let current = target;
  for (let depth = 0; depth < MARK_DEPTH; depth++) {
    const child = onlyChild(current);
    if (!child) break;
    const style = view.getComputedStyle(current);
    if (
      style.display !== 'contents' &&
      !(unpainted(style) && sameBox(current, child))
    )
      break;
    current = child;
  }
  return current;
}
